import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { subscribeNewsletter } from "@/lib/newsletter.functions";
import { toast } from "sonner";

export function NewsletterCTA({ variant = "default" }: { variant?: "default" | "inline" }) {
  const subscribe = useServerFn(subscribeNewsletter);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await subscribe({ data: { email, source: variant } });
      if (res.alreadySubscribed) {
        toast.success("You're already on the list. See you Friday.");
      } else if ((res as { resubscribed?: boolean }).resubscribed) {
        toast.success("Welcome back — you're resubscribed.");
      } else {
        toast.success("You're subscribed. Check your inbox for a welcome note.");
      }
      setEmail("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not subscribe.");
    } finally {
      setSubmitting(false);
    }
  }

  if (variant === "inline") {
    return (
      <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2 max-w-md">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@agency.com"
          className="flex-1 border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <button
          type="submit"
          disabled={submitting}
          className="bg-foreground text-background px-4 py-2 text-sm font-medium hover:bg-ochre-deep transition-colors disabled:opacity-50"
        >
          {submitting ? "..." : "Subscribe"}
        </button>
      </form>
    );
  }

  return (
    <section id="newsletter" className="my-16 border-y-2 border-foreground py-12 px-6 sm:px-10 scroll-mt-24">
      <div className="grid md:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
        <div>
          <p className="eyebrow">The Friday brief</p>
          <h3 className="headline-display mt-3 text-3xl sm:text-4xl leading-tight">
            What changed in local search this week.
          </h3>
          <p className="mt-3 text-foreground/75">
            A short, edited briefing every Friday for local SEO agencies, GBP specialists, and
            multi-location operators. Google Business Profile updates, Map Pack ranking shifts,
            reviews policy, and the AI Overviews / AI Mode moves that matter for local. Free, no spam.
          </p>
        </div>
        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <label htmlFor="newsletter-email" className="sr-only">Email address</label>
          <input
            id="newsletter-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@agency.com"
            className="border border-border bg-background px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <button
            type="submit"
            disabled={submitting}
            className="bg-foreground text-background px-5 py-3 text-base font-semibold hover:bg-ochre-deep transition-colors disabled:opacity-50"
          >
            {submitting ? "Subscribing..." : "Subscribe to the brief"}
          </button>
          <p className="text-xs text-muted-foreground">
            Unsubscribe any time. We never share your email.
          </p>
        </form>
      </div>
    </section>
  );
}
